<template>
  <a-modal
    v-if="visible"
    :title="template?.title"
    :visible="visible"
    :destroy-on-close="true"
    width="800px"
    @cancel="closeModal"
  >
    <a-spin v-if="loading" class="loading-spin" style="height: 650px" />

    <div v-else class="flex flex-column gap-3">
      <dynamic-form ref="form" :template="template" :initial-value="initialValue?.answers ?? {}" @save="setAnswers" />

      <div class="w-6" v-if="initialValue?.associate_signature">
        <h4>Associate Signature</h4>

        <associate-signature :available-signature="initialValue.associate_signature" key="associate_signature" />
      </div>

      <template v-if="type === 'annual_skills_check'"
        ><div class="grid">
          <b class="col-6">Training Started At <span class="required-field">*</span></b>
          <a-date-picker
            class="w-full"
            :allowClear="true"
            :format="visualDateFormat"
            placeholder="Training Started At"
            v-model="form.start_date"
            valueFormat="YYYY-MM-DD"
          />
          <b class="col-6">Training Ended At <span class="required-field"></span></b>
          <a-date-picker
            class="w-full"
            :allowClear="true"
            :format="visualDateFormat"
            placeholder="Training Ended At"
            v-model="form.end_date"
            valueFormat="YYYY-MM-DD"
          /></div
      ></template>

      <template v-if="type === 'annual_skills_check' || type === 'performance_review'"
        ><div class="grid">
          <b class="col-6"> Completion Date <span class="required-field"></span></b>
          <a-date-picker
            class="w-full"
            :allowClear="true"
            :format="visualDateFormat"
            :disabled-date="disabledCompletionDate"
            placeholder="Completion Date"
            v-model="form.completion_date"
            valueFormat="YYYY-MM-DD"
          /></div
      ></template>

      <div class="w-6" v-if="initialValue?.author_signature">
        <h4>Previous Admin Signature</h4>

        <associate-signature :available-signature="initialValue.author_signature" key="author_signature" />
      </div>

      <div class="w-6">
        <h4>New Admin Signature</h4>

        <associate-signature
          checkbox-label="I agree that all data is true and correct"
          :available-signature="null"
          ref="signatureRef"
          key="new_author_signature"
          @checked="checked = $event"
          @save-signature="setSignatureId"
        />
      </div>
    </div>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>
        <a-button type="primary" icon="save" @click="save" :loading="loadingSave" :disabled="!checked">Submit</a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import DynamicForm from '@/components/dynamic-form.vue'
import Signature from '@/components/signature.vue'
import { Button, DatePicker, Modal, notification, Spin } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'

const { mapActions } = createNamespacedHelpers('document')

export default {
  components: {
    'a-modal': Modal,
    'a-button': Button,
    'a-spin': Spin,
    'a-date-picker': DatePicker,
    'associate-signature': Signature,
    'dynamic-form': DynamicForm
  },
  props: {
    type: {
      type: String,
      default: undefined
    },
    associateId: {
      type: Number,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    },
    initialValue: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      loading: false,
      loadingSave: false,

      filledAnswers: false,
      checked: false,
      template: undefined,
      answers: {},
      adminSignatureKey: 0,
      signature_id: undefined,

      form: {
        start_date: undefined,
        end_date: undefined,
        completion_date: undefined
      }
    }
  },
  computed: {
    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },

    disabledSubmit() {
      if (this.type === 'annual_skills_check') {
        return !this.checked && !this.form.start_date && !this.form.completion_date
      }

      if (this.type === 'performance_review') {
        return !this.checked && !this.form.completion_date
      }

      return !this.checked
    }
  },
  watch: {
    visible: {
      async handler(newVal) {
        if (newVal) {
          await this.fetchTemplate()
          this.form.start_date = undefined
          this.form.end_date = undefined
        }
      }
    },
    initialValue: {
      async handler(newVal) {
        if (newVal && newVal.answers) this.answers = newVal.answers
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['postDynamicForm', 'getDynamicTemplate']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    setSignatureId(signatureId) {
      this.signature_id = signatureId
    },

    setAnswers(answers, error) {
      if (error) {
        this.filledAnswers = false
      } else {
        this.answers = answers
        this.filledAnswers = true
      }
    },

    disabledCompletionDate(current) {
      return current && moment(current).startOf('day').isAfter(moment().startOf('day'))
    },

    closeModal() {
      this.$emit('update:visible', false)
      this.signature_id = undefined
      this.answers = {}
      this.filledAnswers = false
    },

    async fetchTemplate() {
      try {
        this.loading = true

        const payload = {
          type: this.type,
          associate_id: this.initialValue.associate_id
        }

        this.template = await this.getDynamicTemplate(payload)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        this.closeModal()
      } finally {
        this.loading = false
      }
    },

    async save() {
      this.loadingSave = true

      try {
        await this.$refs.form.save()

        if (!this.filledAnswers) {
          throw new Error('Some fields are not filled in or are filled in incorrectly. Please check the fields')
        }

        await this.$refs.signatureRef.saveSignature()

        const formattedAnswers = Object.entries(this.answers).reduce((acc, [key, value]) => {
          if (!value) return acc

          acc[key] = Array.isArray(value) ? value : [value]

          return acc
        }, {})

        const payload = {
          form: {
            type: this.type,
            associate_id: this.initialValue.associate_id,
            answers: formattedAnswers,
            author_signature: this.signature_id,
            associate_signature:
              this.initialValue && this.initialValue.associateSignature
                ? this.initialValue.associate_signature.id
                : null,
            ...this.form
          }
        }

        const data = await this.postDynamicForm(payload)

        this.showNotification('success', 'Info', `${this.template.title} has been sent successfully`)

        this.$emit('save', data)

        this.closeModal()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.loadingSave = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  max-height: 600px;
  overflow: auto;
}
</style>
